<template>
    <!-- data-tour-key lets a guided tour step point at one particular entry ("where Configuration
         lives in the menu") without every item needing an anchor of its own. -->
    <component :is="baseComponent" :to="link.path ? link.path : '/'" :class="{ active: isActive }" tag="li"
        data-tour="sidebar.item" :data-tour-key="link.path">
        <a v-if="isMenu" href="#" class="nav-link sidebar-menu-item" :aria-expanded="!collapsed" data-toggle="collapse"
            @click.prevent="collapseMenu">
            <i :class="link.icon" />
            <p :title="link.name">
                {{ link.name }}
                <b class="caret" />
            </p>
        </a>

        <collapse-transition>
            <div v-if="$slots.default || this.isMenu" v-show="forceOpen || !collapsed">
                <ul class="nav links__nav">
                    <slot />
                </ul>
            </div>
        </collapse-transition>

        <slot v-if="children.length === 0 && !$slots.default && link.path" name="title">
            <component :is="elementType(link, false)" :to="link.path" :class="{ active: link.active }" class="nav-link"
                :target="link.target" :href="link.path" @click.native.exact="linkClick"
                @click.native="handleClick($event, link)">
                <template v-if="addLink">
                    <span class="sidebar-mini-icon">{{ linkPrefix }}</span>
                    <span :class="{ 'sidebar-normal': true, 'has-subtitle': link.subtitle }">
                        <small v-if="link.subtitle" class="sidebar-subtitle">
                            {{ link.subtitle }}
                        </small>
                        <span :title="link.name">{{ link.name }}</span>
                    </span>
                </template>
                <template v-else>
                    <i :class="link.icon" />
                    <p :title="link.name">{{ link.name }}</p>
                </template>
            </component>
        </slot>
    </component>
</template>
<script>
import { CollapseTransition } from "vue2-transitions";

export default {
    name: "SidebarItem",
    components: {
        CollapseTransition
    },
    provide() {
        return {
            addLink: this.addChild,
            removeLink: this.removeChild
        };
    },
    inject: {
        addLink: { default: null },
        removeLink: { default: null },
        autoClose: {
            default: true
        }
    },
    props: {
        menu: {
            type: Boolean,
            default: false
        },
        link: {
            type: Object,
            default: () => {
                return {
                    name: "",
                    path: "",
                    children: []
                };
            }
        },
        forceOpen: Boolean,
        favorites: {
            type: [Array, Boolean],
            default: () => []
        }
    },
    data() {
        return {
            children: [],
            collapsed: true
        };
    },
    computed: {
        baseComponent() {
            return this.isMenu || this.link.isRoute ? "li" : "router-link";
        },
        linkPrefix() {
            if (this.link.prefix !== undefined) return this.link.prefix;
            if (this.link.name) {
                const words = this.link.name.split(" ");
                return words.map(word => word.substring(0, 1)).join("");
            }
            return ``;
        },
        isMenu() {
            return this.link.name == "Favorites" || this.children.length > 0 || this.menu === true;
        },
        isActive() {
            if (this.$route && this.$route.path) {
                const matchingRoute = this.children.find(c =>
                    this.$route.path.startsWith(c.link.path)
                );
                if (matchingRoute !== undefined) {
                    return true;
                }
            }
            return false;
        }
    },
    mounted() {
        if (this.addLink) {
            this.addLink(this);
        }
        if (this.link.collapsed !== undefined) {
            this.collapsed = this.link.collapsed;
        }
        if (this.isActive && this.isMenu) {
            this.collapsed = false;
        }
    },
    destroyed() {
        if (this.$el && this.$el.parentNode) {
            this.$el.parentNode.removeChild(this.$el);
        }
        if (this.removeLink) {
            this.removeLink(this);
        }
    },
    methods: {
        addChild(item) {
            const index = this.$slots.default.indexOf(item.$vnode);
            this.children.splice(index, 0, item);
        },
        removeChild(item) {
            const tabs = this.children;
            const index = tabs.indexOf(item);
            tabs.splice(index, 1);
        },
        elementType(link, isParent = true) {
            if (link.isRoute === false) {
                return isParent ? "li" : "a";
            } else {
                return "router-link";
            }
        },
        linkAbbreviation(name) {
            const matches = name.match(/\b(\w)/g);
            return matches.join("");
        },
        linkClick() {
            if (
                this.autoClose &&
                this.$sidebar &&
                this.$sidebar.showSidebar === true
            ) {
                this.$sidebar.displaySidebar(false);
            }
            this.$emit("link-click");
        },
        collapseMenu() {
            this.collapsed = !this.collapsed;
        },
        collapseSubMenu(link) {
            link.collapsed = !link.collapsed;
        },
        handleClick(event, link) {
            if (!event.ctrlKey && !event.metaKey) return;

            event.preventDefault();

            let favorites = Array.isArray(this.favorites) ? [...this.favorites] : [];

            if (favorites.some(f => f.path === link.path || f === link.path)) {
                favorites = favorites.filter(f => f.path !== link.path && f !== link.path);
            } else {
                favorites.push(link.path);
            }

            this.$emit("toggle-favorite", favorites);
        }
    }
};
</script>
<style>
.sidebar-menu-item {
    cursor: pointer;
}

/* Reserve space for the absolutely-positioned collapse caret so long menu titles
   (e.g. "Accounts Receivable") don't render underneath it. */
.sidebar-menu-item p {
    padding-right: 24px;
}

.sidebar ul.links__nav {
    margin-top: 0;
    padding-top: 10px;
}

.sidebar-subtitle {
    font-weight: 800;
    letter-spacing: .5px;
    display: block;
}

.sidebar-normal.has-subtitle {
    line-height: 1.4 !important;
}

/* Additional fixes for sidebar text truncation */
.nav-link {
    overflow: hidden;
}

/* Ensure subtitle truncation as well */
.sidebar-subtitle {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
</style>
